<template>
  <div class="header">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
    <div
      :hidden="this.$route.params.token || this.user._id === this.oilUser"
      class="back-button"
      @click="folderName || isTask ? gotoTaskList() : gotoProductList()"
    >
      <img src="/img/icons/back_arrow.png" />
    </div>
    <Breadcrumb :is-task="isTask" :tool="tool" />

    <div class="menu-button">
      <b-button @click="goToWorkInstructions" v-if="user.privil === 1">
        {{ $t('work_instructions') }}
      </b-button>

      <b-button
        v-if="!isSideBarOpen"
        icon-left="menu-open"
        @click="openSideBar"
        data-testid="publisher-options-btn"
      >
        {{ $t('tasks_header.options') }}
      </b-button>

      <b-button
        v-else
        icon-left="menu-close"
        @click="closeSideBar"
        data-testid="publisher-options-btn"
      >
        {{ $t('tasks_header.options') }}
      </b-button>
    </div>

    <b-sidebar
      :can-cancel="false"
      type="is-light"
      :open.sync="isSideBarOpen"
      right
      fullheight
    >
      <div class="btn-wrapper">
        <b-button
          v-if="!tool.includes('editor') && !isMultiMoveActive"
          @click="
            () => {
              closeSideBar();
              isProgressModalVisible = true;
            }
          "
          icon-left="progress-check"
        >
          {{ $t('tasks_header.progress') }}
        </b-button>

        <b-button
          v-if="!tool.includes('editor') && !isMultiMoveActive"
          @click="
            () => {
              closeSideBar();
              openUsedInstrumentationModal();
            }
          "
        >
          <img
            class="img-icon"
            src="/img/diagram_icon.png"
            style="width: 20px; height: 20px"
          />
          Instrument Log
        </b-button>

        <b-button
          v-if="
            !tool.includes('editor') && !isJobCheckedOut && !isMultiMoveActive
          "
          @click="() => saveJobOffline()"
          :disabled="computedIsOffline || isPublisherReadOnlyByPermission"
          icon-left="download-lock"
        >
          {{ $t('tasks_header.save_offline') }}
        </b-button>
        <b-button
          :style="
            computedIsOffline
              ? {}
              : hasOfflineModeChanges
              ? {
                  backgroundColor: 'green',
                  color: 'white',
                }
              : {}
          "
          v-if="!tool.includes('editor') && isJobCheckedOut"
          @click="() => checkinJobOffline()"
          :disabled="computedIsOffline || isPublisherReadOnlyByPermission"
          icon-left="upload-lock"
          :type="hasOfflineModeChanges ? 'is-success' : 'is-light'"
        >
          {{ $t('tasks_header.sync_job') }}
        </b-button>

        <b-button
          v-if="!isOrderTasksActive && !isMultiMoveActive"
          v-on:click="showDocumentsModal"
          icon-left="file-document-arrow-right"
          :disabled="computedIsOffline"
        >
          {{ $t('tasks_header.documents') }}
        </b-button>

        <b-button
          v-if="!isOrderTasksActive && !isMultiMoveActive"
          :title="
            isRepairAssessmentEnabledForCurrentLocation()
              ? ''
              : $t('feature_disabled')
          "
          :disabled="!isRepairAssessmentEnabledForCurrentLocation()"
          v-on:click="showRepairAssessment"
          data-testid="publisher-rat-btn"
        >
          {{ $t('repair_assessment') }}
        </b-button>
        <b-button
          v-if="
            !isOrderTasksActive &&
            !isMultiMoveActive &&
            tool.includes('publisher')
          "
          v-on:click="showQRCode"
          icon-left="qrcode"
        >
          {{ $t('view_qr_code') }}
        </b-button>
        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isOrderTasksActive &&
            !isMultiMoveActive
          "
          type="is-info"
          v-on:click="copyFrom(job.id)"
          :disabled="
            computedIsOffline ||
            currentJob.jobCompleted ||
            !canEditJob ||
            isPublisherReadOnlyByPermission
          "
        >
          {{ $t('tasks_header.copy_from') }}
        </b-button>

        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isOrderTasksActive
          "
          :type="isMultiMoveActive ? 'is-success' : 'is-info'"
          v-on:click="toggleMultiMove"
          :disabled="
            computedIsOffline ||
            currentJob.jobCompleted ||
            !canEditJob ||
            isPublisherReadOnlyByPermission
          "
        >
          {{ isMultiMoveActive ? $t('move_selected') : $t('select') }}
        </b-button>

        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isOrderTasksActive &&
            isMultiMoveActive
          "
          :type="isMultiMoveActive ? 'is-danger' : 'is-info'"
          v-on:click="deleteTask"
          :disabled="!canEditJob || isPublisherReadOnlyByPermission"
        >
          {{ $t('delete_selected') }}
        </b-button>

        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isOrderTasksActive &&
            isMultiMoveActive
          "
          :type="isMultiSelect ? 'is-success' : 'is-info'"
          v-on:click="toggleSelectAll"
          :disabled="!canEditJob || isPublisherReadOnlyByPermission"
        >
          {{ isMultiSelect ? $t('deselect_all') : $t('select_all') }}
        </b-button>

        <b-button
          v-if="!tool.includes('inspector') && isMultiMoveActive"
          :type="'is-info'"
          v-on:click="cancel"
        >
          {{ $t('cancel') }}
        </b-button>

        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isMultiMoveActive
          "
          :type="isOrderTasksActive ? 'is-success' : 'is-info'"
          v-on:click="toggleOrderTasks"
          :loading="isLoading"
          :disabled="
            computedIsOffline ||
            currentJob.jobCompleted ||
            !canEditJob ||
            isPublisherReadOnlyByPermission
          "
        >
          {{ isOrderTasksActive ? $t('update') : $t('order') }}
        </b-button>
        <b-button
          v-if="displayFolderButton && !isMultiMoveActive"
          :hidden="isOrderTasksActive || this.readOnly"
          type="is-info"
          @click="addFolder"
          :disabled="
            computedIsOffline ||
            currentJob.jobCompleted ||
            !canEditJob ||
            isPublisherReadOnlyByPermission
          "
        >
          {{ $t('add_folder') }}
        </b-button>
        <b-button
          v-if="
            !tool.includes('inspector') &&
            canShowNonInspectorOptions &&
            !isMultiMoveActive
          "
          :hidden="isOrderTasksActive || this.readOnly"
          type="is-info"
          @click="addTask"
          :disabled="
            computedIsOffline ||
            currentJob.jobCompleted ||
            !canEditJob ||
            isPublisherReadOnlyByPermission
          "
        >
          {{ $t('add_task') }}
        </b-button>
        <b-button
          v-if="
            !folderName &&
            tool.includes('inspector') &&
            (!currentJob.jobCompleted ||
              (currentJob.externalClientCreator &&
                !currentJob.notifiedExternal))
          "
          :hidden="this.readOnly"
          @click="completeJob"
          :disabled="computedIsOffline || !canEditJob"
          type="is-info"
        >
          {{ $t('tasks_header.complete_job') }}
        </b-button>
        <b-button
          style="border-left: 1px solid"
          v-if="
            !folderName && tool.includes('publisher') && currentJob.jobCompleted
          "
          :hidden="this.readOnly"
          type="is-info"
          @click="reopenJob"
          :disabled="isPublisherReadOnlyByPermission"
        >
          {{ $t('tasks_header.reopen_job') }}
        </b-button>
        <template v-if="isDeletedTasksAvailable && !isMultiMoveActive">
          <b-button
            type="is-success"
            size="is-large"
            icon-left="recycle"
            class="dij-task-recycle-bin"
            @click="showDeletedTasks"
            :disabled="computedIsOffline"
          />
        </template></div
    ></b-sidebar>

    <b-modal
      :active="isDeletedTasksVisible"
      has-modal-card
      trap-focus
      class="dij-task-deleted-modal"
      @close="hideDeletedTasks"
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <b-icon icon="recycle" size="is-large" />
          <p class="modal-card-title">{{ $t('recycle_bin') }}</p>
        </header>
        <section class="modal-card-body">
          <template v-if="deletedTasks.length > 0">
            <b-table :data="deletedTasks">
              <template slot-scope="props">
                <b-table-column field="taskName" :label="$t('task_name')">
                  {{ props.row.taskName }}
                </b-table-column>
                <b-table-column field="taskType" :label="$t('task_type')">
                  {{ props.row.taskType }}
                </b-table-column>
                <b-table-column field="deletedBy" :label="$t('deleted_by')">
                  {{ props.row.deletedBy }}
                </b-table-column>
                <b-table-column field="deletedAt" :label="$t('deleted_at')">
                  {{ convertEpochToDate(props.row.deletedAt) }}
                </b-table-column>
                <b-table-column :label="$t('actions')">
                  <b-tooltip label="Recover" position="is-top">
                    <b-button
                      class="dij-recycle-bin-recover"
                      type="is-primary"
                      icon-left="backup-restore"
                      @click="undelete(props.row._id)"
                    />
                  </b-tooltip>
                </b-table-column>
              </template>
            </b-table>
          </template>
          <template v-else>
            {{ $t('recycle_bin_empty') }}
          </template>
        </section>
      </div>
    </b-modal>
    <repair-assessment-dialog
      v-if="
        !tool.includes('inspector') &&
        isRepairAssessmentEnabledForCurrentLocation() &&
        isRepairAssessmentVisible
      "
      :read-only="readOnly || !canEditJob"
      :tool="tool"
      v-on:close="hideRepairAssessment"
    />
    <repair-assessment-detail-dialog
      v-if="
        tool.includes('inspector') &&
        isRepairAssessmentEnabledForCurrentLocation() &&
        isRepairAssessmentVisible
      "
      class="dij-ra-header-modal"
      :read-only="readOnly || !canEditJob"
      :tool="tool"
      v-on:close="hideRepairAssessment"
    />
    <documents-modal
      v-if="isDocumentModalVisible"
      :isDocumentModalVisible="isDocumentModalVisible"
      @close="closeDocumentModal"
      :toolType="tool"
      :canEditDocuments="canEditJob"
    />

    <progress-modal
      v-if="isProgressModalVisible"
      @onClose="() => (isProgressModalVisible = false)"
      :tool="tool"
    />

    <QRCodeViewer
      v-if="isQRCodeModalVisible"
      @onClose="() => (isQRCodeModalVisible = false)"
      :qrString="currentJob._id"
    />

    <UsedInstrumentationsModal
      v-if="isUsedInstrumentationModalVisible"
      @close="closeUsedInstrumentationModal"
      :readOnly="isPublisherReadOnlyByPermission"
    />
  </div>
</template>

<script>
import jwt_decode from 'jwt-decode';
import RepairAssessmentDialog from './RepairAssessmentDialog';
import RepairAssessmentDetailDialog from './RepairAssessmentDetailDialog';
import FoldersManagerMixin from '../../mixins/FoldersManagerMixin';
import DocumentsModal from '../Shared/DocumentsModal';
import OfflineMixin from '../../mixins/OfflineMixin';
import ProgressModal from '../Shared/ProgressModal';
import QRCodeViewer from './QRCodeViewer';
import UsedInstrumentationsModal from '../Shared/Instrumentations/UsedInstrumentationsModal';
import Breadcrumb from '../Shared/Breadcrumb';
import { getDirtySyncRecords } from '../../services/db';

export default {
  name: 'tasks-header',
  props: {
    isTask: {
      type: Boolean,
      default: false,
    },
    tool: String,
    limit: 10,
  },
  data() {
    return {
      isSideBarOpen: false,
      isOrderTasksActive: false,
      isMultiMoveActive: false,
      isMultiSelect: false,
      isFirstClick: true,
      updateJob: false,
      notHover: true,
      readOnly: false,
      oilUser: '',
      job: {},
      isRepairAssessmentVisible: false,
      isDeletedTasksVisible: false,
      isDocumentModalVisible: false,
      isProgressModalVisible: false,
      isQRCodeModalVisible: false,
      isLoading: false,
      hasOfflineModeChanges: false,
      deleteTasks: false,
      cancelTasks: false,
      isUsedInstrumentationModalVisible: false,
    };
  },
  components: {
    RepairAssessmentDialog,
    RepairAssessmentDetailDialog,
    DocumentsModal,
    ProgressModal,
    QRCodeViewer,
    UsedInstrumentationsModal,
    Breadcrumb,
  },
  mixins: [FoldersManagerMixin, OfflineMixin],
  computed: {
    isJobOwner() {
      return this.getJobId(this.job) === this.user._id;
    },
    canEditJob() {
      return this.$store.getters.canEditJob(this.job, this.tool);
    },
    isPublisherReadOnlyByPermission() {
      return this.tool.includes('publisher') && !this.canEditJob;
    },
    canEditAllTemplates() {
      return this.$store.getters.hasPermission('global.edit_all_templates');
    },
    canShowNonInspectorOptions() {
      if (this.tool.includes('editor')) {
        return true;
      }

      return (
        this.verifyAccess(this.job) ||
        this.getJobId(this.job) === this.user._id ||
        this.isPublisherReadOnlyByPermission
      );
    },
    displayFolderButton() {
      const isEditorTool = this.tool.includes('editor');
      const isPublisherTool = this.tool.includes('publisher');
      const hasAccess = this.verifyAccess(this.job);

      return (
        (isEditorTool || isPublisherTool) &&
        (isEditorTool || hasAccess || this.isJobOwner) &&
        this.respectFolderDepth
      );
    },

    productListText() {
      return this.$route.name.includes('editor')
        ? this.$t('product_template_list')
        : this.$t('work_order_list');
    },
    task() {
      return this.$store.state.task;
    },
    user() {
      return this.$store.state.currentUser;
    },
    jobsLastKey() {
      return this.$store.state.jobsLastKey;
    },
    jobTasks: {
      get() {
        return this.$store.state.currentTasks;
      },
      set(value) {
        this.$store.dispatch('updateTasksCurrentJob', value);
      },
    },
    tasks: {
      get() {
        return this.$store.state.tasks;
      },
      set(value) {
        this.$store.dispatch('setUpdatedTasks', value);
      },
    },
    sites() {
      return this.$store.state.sites;
    },
    deletedTasks() {
      return this.$store.state.deletedTasks;
    },
    isDeletedTasksAvailable() {
      return (
        !this.isTask &&
        !this.readOnly &&
        !this.currentJob.jobCompleted &&
        (this.tool === 'editor' || this.tool === 'publisher') &&
        this.user &&
        this.user.role === 'admin'
      );
    },

    currentJob() {
      return this.$store.state.currentJob;
    },

    announcementBannerVisible() {
      const now = new Date();
      return (
        this.$store.state.announcementBannerVisible &&
        this.$store.state.announcementBannerMessage &&
        this.$store.state.announcementBannerStartDate <= now &&
        this.$store.state.announcementBannerEndDate > now
      );
    },
  },

  async created() {
    // const { id } = this.$route.params;
    // await this.$store.dispatch('getJob', id);
    this.isLoading = true;
    this.job = this.$store.state.currentJob;

    this.$store.dispatch('getSites');

    if (this.isDeletedTasksAvailable) {
      const { id } = this.$route.params;
      this.$store.dispatch('getJobDeletedTasks', { jobId: id });
    }
    this.isLoading = false;
  },
  watch: {
    async computedIsOffline(offline) {
      if (!offline) {
        this.checkForOfflineChanges();
      }
    },

    isDocumentModalVisible(newValue) {
      if (!newValue) {
        this.openSideBar();
      }
    },

    isProgressModalVisible(newValue) {
      if (!newValue) {
        this.openSideBar();
      }
    },

    isRepairAssessmentVisible(newValue) {
      if (!newValue) {
        this.openSideBar();
      }
    },

    announcementBannerVisible(newValue) {
      if (newValue) {
        this.adjustSidebarForAnnouncementBanner();
      }
    },
  },
  async mounted() {
    this.isLoading = false;
    this.oilUser = process.env.VUE_APP_OIL_USER_EMAIL;

    const { token } = this.$route.query;

    if (token) {
      try {
        this.tokenData = jwt_decode(token, process.env.JWT_SECRET);
        this.readOnly = this.tokenData.action === 'view';

        if (this.tokenData.tool !== 'inspector') {
          this.checkApprovalStatus();
        }
      } catch (err) {
        this.$router.push('/signin');
      }
    }

    if (this.tool === 'editor') {
      if (
        this.user.role !== 'admin' &&
        !this.canEditJob &&
        this.user._id !== process.env.VUE_APP_OIL_USER_EMAIL &&
        !this.canEditAllTemplates
      ) {
        this.readOnly = true;
      }

      return false;
    }

    if (!this.computedIsOffline) {
      this.checkForOfflineChanges();
    }

    if (this.announcementBannerVisible) {
      this.adjustSidebarForAnnouncementBanner();
    }
  },

  methods: {
    goToWorkInstructions() {
      const routeQuery = this.$route.query.token
        ? { token: this.$route.query.token }
        : undefined;

      if (this.tool.includes('publisher')) {
        this.$router.push({
          name: 'publisher-work-instructions',
          params: { id: this.job._id },
          query: routeQuery,
        });
      } else if (this.tool.includes('inspector')) {
        this.$router.push({
          name: 'inspector-work-instructions',
          params: { id: this.job._id },
          query: routeQuery,
        });
      } else if (this.tool.includes('editor')) {
        this.$router.push({
          name: 'editor-work-instructions',
          params: { id: this.job._id },
          query: routeQuery,
        });
      }
    },

    closeUsedInstrumentationModal() {
      this.isUsedInstrumentationModalVisible = false;
    },

    openUsedInstrumentationModal() {
      this.isUsedInstrumentationModalVisible = true;
    },

    openSideBar() {
      this.isSideBarOpen = true;
      this.$emit('sidebarOpened');
    },

    closeSideBar() {
      this.isSideBarOpen = false;
      this.$emit('sidebarClose');
    },

    adjustSidebarForAnnouncementBanner() {
      const banner = document.querySelector('.dij-announcement-banner');
      if (banner) {
        const { height } = banner.getBoundingClientRect();

        const sidebar = document.querySelector(
          '.sidebar-content.is-light.is-fixed.is-fullheight.is-right'
        );
        if (sidebar) {
          sidebar.style.marginTop = `${height + 50}px`;
          sidebar.style.setProperty(
            'margin-top',
            `${height + 50}px`,
            'important'
          );
        }
      }
    },

    async checkForOfflineChanges() {
      const dirtySyncRecords = await getDirtySyncRecords(true);

      let hasChanges = false;
      dirtySyncRecords.forEach((record) => {
        if (record.checkedJobId) {
          hasChanges = true;
        }
      });

      this.hasOfflineModeChanges = hasChanges;

      if (hasChanges) {
        this.$buefy.toast.open({
          duration: 5000,
          message:
            'Unsynced job changes found. Sync soon to keep data up to date.',
          type: 'is-success',
          position: 'is-bottom',
        });
      }
    },
    async toggleOrderTasks() {
      if (await this.preventOfflineAction()) {
        return;
      }
      this.isLoading = true;
      if (this.isOrderTasksActive) {
        try {
          this.isOrderTasksActive = false;
          const tasksOrderSnapshotNew = this.snapsShotTasksOrder();
          if (
            !this.areSnapshotsIdentical(
              tasksOrderSnapshotNew,
              this.tasksOrderSnapshot
            )
          ) {
            await this.$store.dispatch('updateTasksOrder', {
              jobId: this.job._id,
              tasks: this.tasks,
              folderId: this.folderId,
            });
            if (!this.isFirstClick) {
              this.updateJob = true;
            }
          }
        } catch (e) {
          this.$store.dispatch('setErrorMessage', e.message);
        }
      } else {
        this.isFirstClick = false;
        this.tasksOrderSnapshot = this.snapsShotTasksOrder();
        this.updateJob = false;
        this.isOrderTasksActive = true;
        this.$store.dispatch('setInfoMessage', this.$t('message_order_drag'));
      }
      this.isLoading = false;
    },

    snapsShotTasksOrder() {
      return this.tasks.map((task) => ({
        id: task._id,
        order: task.taskOrder,
      }));
    },

    areSnapshotsIdentical(snapshotNew, snapshotOld) {
      if (snapshotNew.length !== snapshotOld.length) {
        return false;
      }
      for (let i = 0; i < snapshotNew.length; i++) {
        if (
          snapshotNew[i].id !== snapshotOld[i].id ||
          snapshotNew[i].order !== snapshotOld[i].order
        ) {
          return false;
        }
      }
      return true;
    },

    async copyFrom(id) {
      if (await this.preventOfflineAction()) {
        return;
      }
      if (this.tool === 'editor') {
        this.$store.commit('SET_CURRENT_JOB', this.job);
        this.$router.push({ name: 'editor-copyfrom', params: id });
      } else {
        this.$store.commit('SET_CURRENT_JOB', this.job);
        this.$router.push({ name: 'publisher-copyfrom', params: id });
      }
    },

    async toggleMultiMove() {
      if (await this.preventOfflineAction()) {
        return;
      }
      if (this.isMultiMoveActive) {
        this.isMultiMoveActive = false;
      } else {
        this.isMultiMoveActive = true;
        this.isMultiSelect = false;
      }
    },

    cancel() {
      if (this.cancelTasks) {
        this.cancelTasks = false;
      } else {
        this.cancelTasks = true;
      }
      this.isMultiMoveActive = false;
    },

    toggleSelectAll() {
      if (this.isMultiSelect) {
        this.isMultiSelect = false;
      } else {
        this.isMultiSelect = true;
      }
    },

    deleteTask() {
      if (this.deleteTasks === false) {
        this.deleteTasks = true;
      } else {
        this.deleteTasks = false;
      }
      this.isMultiMoveActive = false;
    },

    gotoProductList() {
      this.isLoading = true;
      if (this.tool.includes('editor')) {
        this.$router.push({
          name: 'editor-list',
        });
      } else if (this.tool.includes('inspector')) {
        this.$router.push({
          name: 'inspector-list',
        });
      } else if (this.tool.includes('publisher')) {
        this.$router.push({
          name: 'publisher-list',
        });
      } else {
        this.$router.push({
          name: 'report-list',
        });
      }
    },
    gotoTaskList(task = false, folder = false) {
      this.isLoading = true;
      if (!this.folderName && !this.isTask) {
        return false;
      }

      const { id } = this.$route.params;
      const routeName = `${this.tool}-tasks`; // e.g: 'editor-tasks'
      const { token } = this.$route.query || {};
      if (folder && !this.isTask) {
        const path = `/view/${this.tool}/${id}/folder/${this.folderId}`;
        const query = token ? { token } : undefined;
        return this.$router.push({ path, query });
      }

      if (!task && this.task.folderId) {
        const path = `/view/${this.tool}/${id}/folder/${this.task.folderId}`;
        const query = token ? { token } : undefined;
        return this.$router.push({ path, query });
      }

      if (this.subfolderId) {
        const path = `/view/${this.tool}/${id}/folder/${this.folderId}`;
        const query = token ? { token } : undefined;
        return this.$router.push({ path, query });
      }

      this.$store.commit('SET_TASK_OBJECT', {});
      const params = { id };
      const query = token ? { token } : undefined;
      this.$router.push({ name: routeName, params, query });
    },
    async addFolder() {
      if (await this.preventOfflineAction()) {
        return;
      }

      let path = `/view/${this.tool}/${this.job._id}/create/folder`;

      if (this.folderId) {
        path += `/${this.folderId}`;
      }

      const routeParams = {
        path,
        params: {
          id: this.job._id,
          folderId: this.subfolderId || this.folderId,
        },
      };

      if (this.$route.query.token) {
        routeParams.query = {
          token: this.$route.query.token,
        };
      }

      this.$router.push(routeParams);
    },
    async addTask() {
      if (await this.preventOfflineAction()) {
        return;
      }

      let routeTool = '';
      if (this.tool.includes('editor')) {
        routeTool = 'editor';
      } else if (this.tool.includes('publisher')) {
        routeTool = 'publisher';
      }

      const query = {};
      if (this.folderId) {
        query.folderId = this.folderId;
      }
      if (this.subfolderId) {
        query.folderId = this.subfolderId;
      }

      const params = {
        name: `${routeTool}-create-task`,
        params: { id: this.job._id },
        query,
      };

      if (this.$route.query.token) {
        params.query.token = this.$route.query.token;
      }

      this.$router.push(params);
    },
    checkApprovalStatus() {
      if (
        this.job?.approvalStatus === 'approved' ||
        this.job?.approvalStatus === 'requested'
      ) {
        this.readOnly = true;
      }
    },
    reopenTask(task) {
      task.workStatusCode = 0;
      this.$store.dispatch('updateTask', { task, updateState: false });
    },
    isTaskCompleted(task) {
      return task.workStatusCode
        ? task.workStatusCode.toString() === '2'
        : false;
    },
    async reopenJob() {
      // New change from Marco, do not reopen tasks unless they are opend manually
      // const tasks = await this.$store.dispatch('getJobTasks', {
      //   jobId: this.job._id,
      //   allTasks: true,
      //   return: true,
      // });

      // update shown tasks to visual change the progress
      // this.tasks.forEach((task) => {
      //   if (this.isTaskCompleted(task)) this.reopenTask(task);
      // });
      // // update other tasks not shown
      // tasks.forEach((task) => {
      //   if (this.isTaskCompleted(task)) this.reopenTask(task);
      // });
      this.isLoading = true;
      // const newJob = { ...this.job };
      // newJob.jobCompleted = false;
      // newJob.jobCompleteDate = null;
      // newJob.jobCompletedByUser = '';

      try {
        this.job = await this.$store.dispatch('reopenJob', this.job._id);
        this.$store.dispatch(
          'setSuccessMessage',
          this.$t('tasks_header.job_reopened_successfully')
        );
      } catch (err) {
        this.$store.dispatch(
          'setErrorMessage',
          this.$t('tasks_header.failed_reopen_job')
        );
        console.error('Failed to reopen job', err);
      } finally {
        this.isLoading = false;
      }
    },
    completeJob() {
      if (!this.canEditJob) {
        return;
      }

      let prompt = false;
      this.tasks.forEach((task) => {
        if (task.workStatusCode !== '2') prompt = true;
      });
      if (prompt) {
        return this.$buefy.dialog.confirm({
          message: this.$t('tasks_header.complete_job_confirmation'),
          type: 'is-success',
          hasIcon: true,
          icon: 'check',
          confirmText: this.$t('yes'),
          onConfirm: async () => {
            return this.signOff();
          },
        });
      }
      return this.signOff();
    },
    async signOff() {
      this.isLoading = true;
      this.$store.dispatch('setWaitingForWrite', true);
      const tasks = await this.$store.dispatch('getJobTasks', {
        jobId: this.job._id,
        allTasks: true,
        return: true,
      });

      // update shown tasks to visual change the progress
      this.tasks.forEach((task) => {
        if (task.workStatusCode !== '2') this.signOffTask(task);
      });

      // update other tasks not shown
      tasks.forEach((task) => {
        if (task.workStatusCode !== '2') this.signOffTask(task);
      });
      const newJob = { ...this.job };
      newJob.jobCompleted = true;
      newJob.jobCompleteDate = new Date();
      newJob.jobCompletedByUser = newJob.prodCreatedByUser || 'N/A';

      if (this.tokenData?.referral) {
        const externalClientName = this.tokenData.referral.split('_')[0];

        if (externalClientName) {
          try {
            const data = { client: externalClientName, id: this.job._id };
            await this.$store.dispatch('notifyJobCompleted', data);
            newJob.notifiedExternal = true;
            this.$store.dispatch(
              'setSuccessMessage',
              this.$t('tasks_header.job_completed_notification', {
                client: externalClientName,
              })
            );
          } catch (err) {
            this.$store.dispatch(
              'setErrorMessage',
              `Failed to send notification to ${externalClientName}`
            );
            console.error('Failed to update job', err);
            return false;
          }
        }
      }

      try {
        this.job = await this.$store.dispatch('updateJobSimple', newJob);
        this.$store.dispatch('setSuccessMessage', 'Job completed successfuly');
      } catch (err) {
        this.$store.dispatch('setErrorMessage', 'Failed to complete job');
        console.error('Failed to update job', err);
      }
      this.$store.dispatch('setWaitingForWrite', false);
      this.isLoading = false;
    },
    verifyAccess(job) {
      if (this.tool !== 'editor') {
        return true;
      }

      if (this.canEditAllTemplates) {
        return true;
      }

      const isAdmin = this.user.role === 'admin';
      const isSameLocation =
        job.prodCreatedByUser &&
        this.user.location === job.prodCreatedByUser.location;
      const isOilUser = this.user._id === process.env.VUE_APP_OIL_USER_EMAIL;

      if (isAdmin || this.user.privil === 1 || isSameLocation || isOilUser) {
        return true;
      }

      return false;
    },
    getJobId(job) {
      if (job.prodCreatedByUser && job.prodCreatedByUser._id) {
        return job.prodCreatedByUser._id;
      }
      return '';
    },
    async signOffTask(task) {
      try {
        const taskToBeUpdated = { ...task };
        taskToBeUpdated.workStatusCode = 2;
        if (
          !taskToBeUpdated.signedBy ||
          taskToBeUpdated.signedBy === '' ||
          taskToBeUpdated.signedBy === '-'
        ) {
          taskToBeUpdated.signedBy = this.user.fullname;
          taskToBeUpdated.signedAt = new Date();
        }
        await this.$store.dispatch('updateTask', taskToBeUpdated);
        task.workStatusCode = 2;
      } catch (e) {
        this.$store.dispatch('setErrorMessage', e.message);
      }
    },
    async showRepairAssessment() {
      this.closeSideBar();
      if (this.job.repairAssessment) {
        this.job.repairAssessment.forEach((ra) => {
          if (ra.serialNumber === undefined && !ra.isMinor) {
            ra.serialNumber = '';
          }
          if (ra.assembly === undefined) {
            ra.assembly = '';
          }
          if (ra.itemNumber === undefined) {
            ra.itemNumber = '';
          }
        });
      }
      this.isRepairAssessmentVisible = true;
    },
    showQRCode() {
      this.closeSideBar();
      this.isQRCodeModalVisible = true;
    },
    async showDocumentsModal() {
      this.closeSideBar();
      if (await this.preventOfflineAction()) {
        return;
      }
      this.isDocumentModalVisible = true;
    },
    closeDocumentModal() {
      this.isDocumentModalVisible = false;
    },
    hideRepairAssessment() {
      this.isRepairAssessmentVisible = false;
    },
    isRepairAssessmentEnabledForCurrentLocation() {
      if (!this.user || !this.sites) {
        return false;
      }
      if (this.user.privil === 1) {
        return true;
      }
      if (this.sites) {
        const currentLocation = this.sites.filter(
          (site) => site._id === this.user.location
        )[0];

        if (currentLocation) {
          return currentLocation.repairAssessmentEnabled;
        }
        return false;
      }

      return false;
    },
    showDeletedTasks() {
      this.closeSideBar();
      this.isDeletedTasksVisible = true;
    },
    hideDeletedTasks() {
      this.isDeletedTasksVisible = false;
    },
    async undelete(taskId) {
      await this.$store.dispatch('undeleteTask', taskId);
      const { id } = this.$route.params;

      const optionsForJobTasks = { jobId: id };
      if (this.folderId) {
        optionsForJobTasks.folderId = this.folderId;
      }

      this.$store.dispatch('getJobTasks', optionsForJobTasks);
      this.$store.dispatch('getJobDeletedTasks', { jobId: id });
    },
    convertEpochToDate(epoch) {
      const date = new Date();
      date.setTime(epoch * 1000);
      return date
        .toISOString()
        .slice(0, 19)
        .replace(/-/g, '/')
        .replace('T', ' ');
    },
  },
};
</script>
<style lang="scss" scoped>
.b-tooltips {
  .b-tooltip:not(:last-child) {
    margin-right: 0.5em;
  }
  .b-tooltip {
    margin-bottom: 0.5em;
  }
}

.menu-button {
  display: flex;
  align-items: center;
  margin-right: 5px;
  gap: 10px;
}

.btn-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 5px;
}
pre {
  margin: 0;
  padding: 0;
  background: none;
}
.header {
  z-index: 2;
  font-family: 'Siemens Sans';
  display: inline-flex;
  position: relative;
  width: 100%;
  height: 50px;
  background-color: #fafafa;
  font-size: 18px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
  color: #333;
  .back-button {
    display: inline-flex;
    width: 80px;
    height: 100%;
    cursor: pointer;
    img {
      width: 25px;
      margin: auto;
      &:hover {
        filter: brightness(0%);
      }
    }
  }
}

.dij-task-recycle-bin.button.is-success {
  border-left: 1px solid;
  height: 50px;
  padding-top: 0;
  padding-bottom: 0;
}

.dij-task-deleted-modal .modal-card {
  width: 960px;

  .modal-card-body {
    font-size: 16px;
  }
}
</style>

<style lang="scss">
.sidebar-content.is-light.is-fixed.is-fullheight.is-right {
  margin-top: 50px !important;
  top: 50px !important;
  z-index: 1 !important;
}
</style>
